import { hasShortcutsAccess } from "@faire/help-center-utils/serialized-data/shortcutsAccess";
import { Grid } from "@faire/web--source/slate/Layout";
import { useResponsiveValue } from "@faire/web--source/ui/hooks/useResponsiveValue";
import React, { useMemo } from "react";

import { useIsSelfServeHomePage } from "../hooks/useIsSelfServeHomePage";
import { useSelfServeContext } from "../hooks/useSelfServeContext";
import { SelfServeNode } from "../types";

import { NodeChildrenRenderer } from "./NodeChildrenRenderer";

interface IProps {
  parentNode: SelfServeNode | undefined;
  selectedNode: SelfServeNode | undefined;
  setSelectedNode: (node: SelfServeNode) => void;
  isAutofocusOnMobile?: boolean;
  bottomElementRef?: React.MutableRefObject<HTMLElement | null>;
  loadTimingRef?: React.RefCallback<HTMLElement>;
}

export const IssueTopics: React.FC<IProps> = ({
  parentNode,
  selectedNode,
  setSelectedNode,
  isAutofocusOnMobile,
  bottomElementRef,
  loadTimingRef,
}) => {
  const itemsPerRowHome = useResponsiveValue({
    mobileAndAbove: 2,
    tabletAndAbove: 3,
    desktopAndAbove: 4,
    xLargeAndAbove: 5,
  });

  const itemsPerRowHomeWithShortcuts = useResponsiveValue({
    mobileAndAbove: 2,
    desktopAndAbove: 2,
  });

  const itemsPerRowOthers = useResponsiveValue({
    mobileAndAbove: 1,
    tabletAndAbove: 2,
  });

  const { context } = useSelfServeContext();
  const isSelfServeHomePage = useIsSelfServeHomePage();
  const isFairePrograms = parentNode?.id === "FairePrograms";
  const displayInTwoColumns = isFairePrograms || isSelfServeHomePage;

  const columns = useMemo(() => {
    let column;
    if (displayInTwoColumns) {
      column = hasShortcutsAccess()
        ? itemsPerRowHomeWithShortcuts
        : itemsPerRowHome;
    } else {
      column = itemsPerRowOthers;
    }
    return column;
  }, [
    displayInTwoColumns,
    itemsPerRowHome,
    itemsPerRowHomeWithShortcuts,
    itemsPerRowOthers,
  ]);
  return (
    <Grid
      ref={loadTimingRef}
      gap="2x"
      columns={columns}
      style={{
        width: "100%",
        gridAutoRows: displayInTwoColumns
          ? "minmax(100px, auto)"
          : "minmax(55px, auto)",
      }}
    >
      <NodeChildrenRenderer
        context={context}
        node={parentNode}
        selectedNode={selectedNode}
        onClickOnNode={setSelectedNode}
        isAutofocusOnMobile={isAutofocusOnMobile}
        bottomElementRef={bottomElementRef}
      />
    </Grid>
  );
};
